import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  LayoutDashboard,
  Menu,
  Settings,
  Video,
  X,
} from "lucide-react";

import logoDrizaCamp from "../assets/logo-driza-camp.png";

const GROUPS = [
  {
    label: "Clientes",
    paths: ["/clientes", "/nuevocliente"],
    items: [
      { label: "Clientes", path: "/clientes" },
      { label: "Nuevo cliente", path: "/nuevocliente" },
    ],
  },
  {
    label: "Movimientos",
    paths: ["/movimientos", "/detallesmovimientos"],
    items: [
      { label: "Movimientos", path: "/movimientos" },
      { label: "Detalles de movimientos", path: "/detallesmovimientos" },
    ],
  },
  {
    label: "Restaurante",
    paths: ["/restaurante"],
    items: [
      { label: "Salón y mesas", path: "/restaurante" },
      { label: "Cocina", path: "/restaurante/cocina" },
      { label: "Configurar salones", path: "/restaurante/configuracion" },
    ],
  },
  {
    label: "Cantina",
    paths: ["/ventas", "/cantina/productos", "/cantina/historial", "/cantina/cierre"],
    items: [
      { label: "Nueva venta", path: "/ventas" },
      { label: "Productos e inventario", path: "/cantina/productos" },
      { label: "Historial de ventas", path: "/cantina/historial" },
      { label: "Cierre de Cantina", path: "/cantina/cierre" },
    ],
  },
  {
    label: "Más",
    paths: ["/guarderia", "/caja/cierre", "/dashboard", "/ConfiguracionPage", "/ayuda"],
    items: [
      { label: "Guardería", path: "/guarderia" },
      { label: "Cierre de caja", path: "/caja/cierre" },
      { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
      { label: "Configuración", path: "/ConfiguracionPage", icon: Settings },
      { label: "Videos", path: "/ayuda", icon: Video },
    ],
  },
];

function DesktopDropdown({ group, pathname }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const active = group.paths.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );

  useEffect(() => {
    const handleOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={[
          "flex h-10 whitespace-nowrap items-center gap-1 rounded-lg px-2.5 text-[13px] font-semibold",
          "transition-colors focus:outline-none focus:ring-2 focus:ring-white/70",
          active
            ? "bg-white text-azul-oscuro shadow-sm"
            : "text-white hover:bg-white/15",
        ].join(" ")}
      >
        {group.label}

        <ChevronDown
          size={16}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-50 min-w-[220px] overflow-hidden rounded-xl border border-gray-100 bg-white py-2 shadow-xl">
          {group.items.map((item) => {
            const Icon = item.icon;

            const itemActive =
              pathname === item.path ||
              pathname.startsWith(`${item.path}/`);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                className={[
                  "flex min-h-11 items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors",
                  itemActive
                    ? "bg-green-50 text-verde-oscuro"
                    : "text-gray-700 hover:bg-gray-50 hover:text-azul-oscuro",
                ].join(" ")}
              >
                {Icon && <Icon size={17} />}
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Navbar() {
  const location = useLocation();
  const pathname = location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const directLinkClass = (path) => {
    const active =
      pathname === path ||
      (path !== "/" && pathname.startsWith(`${path}/`));

    return [
      "flex h-10 whitespace-nowrap items-center rounded-lg px-2.5 text-[13px] font-semibold",
      "transition-colors focus:outline-none focus:ring-2 focus:ring-white/70",
      active
        ? "bg-white text-azul-oscuro shadow-sm"
        : "text-white hover:bg-white/15",
    ].join(" ");
  };

  return (
    <nav className="sticky top-0 z-50 w-full bg-gradient-to-r from-azul-oscuro to-verde-esmeralda shadow-md">
      <div className="flex min-h-[68px] w-full min-w-0 items-center gap-3 px-4 xl:px-6">
        <Link
          to="/"
          className="flex shrink-0 items-center"
          aria-label="Ir al inicio"
        >
          <img
            src={logoDrizaCamp}
            alt="Driza Camp"
            className="h-12 w-auto max-w-[140px] object-contain brightness-0 invert"
          />
        </Link>

        <div className="hidden min-w-0 flex-1 items-center justify-evenly gap-1 xl:flex">
          <DesktopDropdown group={GROUPS[0]} pathname={pathname} />
          <DesktopDropdown group={GROUPS[1]} pathname={pathname} />

          <Link to="/registros" className={directLinkClass("/registros")}>
            Ingresos
          </Link>

          <Link to="/historico" className={directLinkClass("/historico")}>
            Histórico
          </Link>

          <DesktopDropdown group={GROUPS[2]} pathname={pathname} />
          <DesktopDropdown group={GROUPS[3]} pathname={pathname} />
          <DesktopDropdown group={GROUPS[4]} pathname={pathname} />
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((value) => !value)}
          className="ml-auto flex h-11 w-11 items-center justify-center rounded-lg text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/70 xl:hidden"
          aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-white/15 bg-azul-oscuro/95 px-4 py-4 xl:hidden">
          <div className="mx-auto flex max-w-xl flex-col gap-2">
            {GROUPS.slice(0, 2).map((group) => (
              <div key={group.label} className="rounded-xl bg-white/10 p-2">
                <div className="px-2 py-1 text-xs font-bold uppercase tracking-wide text-white/60">
                  {group.label}
                </div>

                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-white hover:bg-white/10"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            ))}

            <Link
              to="/registros"
              className="rounded-lg px-4 py-3 font-semibold text-white hover:bg-white/10"
            >
              Ingresos
            </Link>

            <Link
              to="/historico"
              className="rounded-lg px-4 py-3 font-semibold text-white hover:bg-white/10"
            >
              Histórico
            </Link>

            {GROUPS.slice(2).map((group) => (
              <div key={group.label} className="rounded-xl bg-white/10 p-2">
                <div className="px-2 py-1 text-xs font-bold uppercase tracking-wide text-white/60">
                  {group.label}
                </div>

                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-white hover:bg-white/10"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
